import { FEED_NEWS_CACHE_MAX_ITEMS } from './constants'
import { sortNewsByPubDate } from './mergeFeedNews'
import type { NewsItem } from './types'

const CACHE_PREFIX = 'synap-feed-news-v3:'
/** Migração única da versão em sessionStorage (v2). */
const LEGACY_SESSION_PREFIX = 'synap-feed-news-v2:'

type StoredItem = Omit<NewsItem, 'pubDate'> & { pubDate: string | null }

type StoredPayload = {
  savedAt: string
  items: StoredItem[]
  lastSyncReferenceAt?: string
}

function storage(): Storage | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage
}

function deserializeItems(items: StoredItem[]): NewsItem[] {
  return items
    .filter((it) => it && typeof it === 'object' && typeof it.title === 'string')
    .map((it) => ({
      ...it,
      pubDate: it.pubDate ? new Date(it.pubDate) : null,
    }))
}

function migrateLegacySessionCache(userId: string): NewsItem[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(LEGACY_SESSION_PREFIX + userId)
    if (!raw) return []
    const parsed = JSON.parse(raw) as { items?: StoredItem[] }
    if (!Array.isArray(parsed.items)) return []
    const items = deserializeItems(parsed.items)
    sessionStorage.removeItem(LEGACY_SESSION_PREFIX + userId)
    if (items.length) saveFeedNewsCache(userId, items)
    return items
  } catch {
    return []
  }
}

/** Aplica teto de itens após ordenação por data (mais recentes primeiro). */
export function capFeedNewsItems(items: NewsItem[], max = FEED_NEWS_CACHE_MAX_ITEMS): NewsItem[] {
  if (items.length <= max) return items
  return sortNewsByPubDate(items).slice(0, max)
}

export function loadFeedNewsCache(userId: string): NewsItem[] {
  const store = storage()
  if (!store) return []
  try {
    const raw = store.getItem(CACHE_PREFIX + userId)
    if (!raw) return migrateLegacySessionCache(userId)
    const parsed = JSON.parse(raw) as StoredPayload
    if (!Array.isArray(parsed.items)) return []
    return capFeedNewsItems(deserializeItems(parsed.items))
  } catch {
    return []
  }
}

export function loadFeedNewsCacheMeta(userId: string): { lastSyncReferenceAt: Date | null } {
  const store = storage()
  if (!store) return { lastSyncReferenceAt: null }
  try {
    const raw = store.getItem(CACHE_PREFIX + userId)
    if (!raw) return { lastSyncReferenceAt: null }
    const parsed = JSON.parse(raw) as StoredPayload
    const ref = parsed.lastSyncReferenceAt
    if (!ref) return { lastSyncReferenceAt: null }
    const d = new Date(ref)
    return { lastSyncReferenceAt: Number.isNaN(d.getTime()) ? null : d }
  } catch {
    return { lastSyncReferenceAt: null }
  }
}

export function saveFeedNewsCache(
  userId: string,
  items: NewsItem[],
  meta?: { lastSyncReferenceAt?: Date },
): void {
  const store = storage()
  if (!store) return
  try {
    const capped = capFeedNewsItems(items)
    const stored: StoredItem[] = capped.map((it) => ({
      ...it,
      pubDate: it.pubDate ? it.pubDate.toISOString() : null,
    }))
    const payload: StoredPayload = {
      savedAt: new Date().toISOString(),
      items: stored,
    }
    if (meta?.lastSyncReferenceAt) {
      payload.lastSyncReferenceAt = meta.lastSyncReferenceAt.toISOString()
    }
    store.setItem(CACHE_PREFIX + userId, JSON.stringify(payload))
  } catch {
    // quota / private mode
  }
}

/** Remove cache de notícias (ação «Limpar notícias salvas»). */
export function clearFeedNewsCache(userId: string): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(CACHE_PREFIX + userId)
  } catch {
    // ignore
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(LEGACY_SESSION_PREFIX + userId)
    } catch {
      // ignore
    }
  }
}
